import React, { useEffect, useState, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { NavLink } from "react-router-dom";
import {
  FiBriefcase,
  FiCalendar,
  FiCamera,
  FiChevronRight,
  FiFileText,
  FiHome,
  FiLock,
  FiMail,
  FiMapPin,
  FiPhone,
  FiSave,
  FiShield,
  FiUser,
} from "react-icons/fi";
import {
  getAdminProfile,
  updateAdminProfile,
  uploadSingleFile,
} from "../Services/userService";
import { toast } from "react-toastify";
import Skeleton from "../Components/SkeletonLoader";
import { RoutesPaths, PROFILE_UPDATED_EVENT } from "../Router/constant";

// DOB must be at least 10 years ago.
const maxDob = () => new Date(new Date().setFullYear(new Date().getFullYear() - 10));

export default function Profile() {
  const [profileData, setProfileData] = useState({
    fullName: "",
    email: "",
    dob: "",
    profileImage: "",
    role: "",
    contactPersonName: "",
    companyName: "",
    businessRegistrationNumber: "",
    phone: "",
    headquartersAddress: "",
    serviceTypes: [],
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [sendPreviewImage, setSendPreviewImage] = useState();
  const [loading, setLoading] = useState(true);
  const [dobDate, setDobDate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fileInputRef = useRef(null);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const profileResp = await getAdminProfile();
      const adminData =
        profileResp.data?.data?.admin || profileResp?.data?.data?.user;
      const role = profileResp.data.data?.role;
      const firstAdmin = Array.isArray(adminData) ? adminData[0] : adminData;
      if (firstAdmin) {
        setProfileData({
          fullName: firstAdmin?.fullName || "",
          email: firstAdmin?.email || "",
          dob: firstAdmin?.dob || "",
          profileImage: firstAdmin?.profileImage || "",
          role: role || "",
          contactPersonName: firstAdmin?.contactPersonName || "",
          companyName: firstAdmin?.companyName || "",
          businessRegistrationNumber: firstAdmin?.businessRegistrationNumber || "",
          phone: firstAdmin?.phone || "",
          headquartersAddress: firstAdmin?.headquartersAddress?.address || "",
          serviceTypes: firstAdmin?.serviceTypes || [],
        });

        if (firstAdmin.profileImage) {
          setPreviewImage(firstAdmin.profileImage);
        }

        if (firstAdmin.dob) {
          setDobDate(new Date(firstAdmin.dob));
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (date) => {
    setDobDate(date);
    setProfileData((prev) => ({
      ...prev,
      dob: date ? date.toISOString() : "",
    }));
  };

  const handleImageClick = () => {
    if (!uploading) fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file || !file.type.startsWith("image/")) return;

    setUploading(true);
    try {
      const reader = new FileReader();

      const fileAsBase64 = await new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => reject("Failed to read file");
        reader.readAsDataURL(file);
      });
      const payload = { base64: fileAsBase64 };
      const response = await uploadSingleFile(payload);
      const fileName = response?.data?.data?.fileName;
      if (response?.status === 200 && fileName) {
        setPreviewImage(fileName);
        setSendPreviewImage(fileName);
      } else {
        console.error("Upload failed or fileName not found in response");
        toast.error("Unable to upload image.");
      }
    } catch (error) {
      console.error("Image upload failed:", error);
      toast.error("Unable to upload image.");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...profileData,
        profileImage: sendPreviewImage,
      };
      await updateAdminProfile(payload);
      toast.success("Profile updated successfully!");

      // Let the header (and anything else showing the profile) refresh without a reload.
      window.dispatchEvent(
        new CustomEvent(PROFILE_UPDATED_EVENT, {
          detail: {
            profileImage: sendPreviewImage || previewImage || "",
            fullName: profileData.fullName,
            contactPersonName: profileData.contactPersonName,
          },
        }),
      );
    } catch (err) {
      console.error("Update failed:", err);
      toast.error("Error updating profile.");
    } finally {
      setSaving(false);
    }
  };

  const isPartner = profileData.role === "partner";
  const isAdmin = profileData.role === "admin";
  const displayName = profileData.fullName || profileData?.contactPersonName || "N/A";

  const renderField = ({ label, Icon, locked, children }) => (
    <div className="qv-field">
      <span className="qv-field-icon">
        <Icon />
      </span>
      <div className="qv-field-body">
        <label className="qv-label">
          {label}
          {locked && (
            <span className="qpr-locked">
              <FiLock /> Not editable
            </span>
          )}
        </label>
        {loading ? <Skeleton width="100%" height={40} /> : children}
      </div>
    </div>
  );

  return (
    <div className="container-fluid qd-page">
      {/* Breadcrumb */}
      <nav className="qd-breadcrumb">
        <NavLink to={RoutesPaths.DASHBOARD} aria-label="Dashboard">
          <FiHome />
        </NavLink>
        <FiChevronRight />
        <span className="qd-breadcrumb-current">Settings</span>
      </nav>

      <div className="qd-page-header">
        <div>
          <h1 className="qd-title">Profile settings</h1>
          <p className="qd-subtitle">Update your username and manage your account.</p>
        </div>
      </div>

      {/* Profile header */}
      <div className="qd-card qpr-hero">
        <button
          type="button"
          className="qpr-avatar"
          onClick={handleImageClick}
          disabled={loading || uploading}
          aria-label="Change profile photo"
          title="Change profile photo"
        >
          {loading ? (
            <Skeleton circle width={96} height={96} />
          ) : (
            <img
              src={
                previewImage
                  ? `${process.env.REACT_APP_IMAGE_URL}/${previewImage}`
                  : "/assets/img/download.png"
              }
              alt=""
            />
          )}
          {uploading && <span className="qpr-avatar-overlay"><span className="qo-spinner" /></span>}
          <span className="qpr-camera">
            <FiCamera />
          </span>
        </button>
        <input
          type="file"
          accept="image/*"
          hidden
          ref={fileInputRef}
          onChange={handleFileChange}
        />

        <div className="qpr-identity">
          {loading ? (
            <>
              <Skeleton width={200} height={24} />
              <div style={{ height: 8 }} />
              <Skeleton width={120} height={22} />
            </>
          ) : (
            <>
              <h2>{displayName}</h2>
              <div className="qu-pills">
                <span className="qu-pill qu-pill-active">
                  <FiShield /> {isAdmin ? "Admin" : "Vendor"}
                </span>
              </div>
              {isPartner && profileData?.serviceTypes?.length > 0 && (
                <div className="qpd-chips">
                  {profileData.serviceTypes.map((service, index) => (
                    <span className="qs-type" key={index}>
                      {service}
                    </span>
                  ))}
                </div>
              )}
              <p className="qpr-hint">Click the photo to upload a new one (JPG or PNG).</p>
            </>
          )}
        </div>
      </div>

      {/* Details form */}
      <section className="qd-card qv-section">
        <div className="qv-section-head">
          <span className="qv-section-icon">
            <FiUser />
          </span>
          <div>
            <h6>Account details</h6>
            <p>Keep your personal{isPartner ? " and company" : ""} information up to date.</p>
          </div>
        </div>

        <div className="qv-grid">
          {isPartner && (
            <>
              {renderField({
                label: "Company name",
                Icon: FiBriefcase,
                children: (
                  <input
                    type="text"
                    name="companyName"
                    className="qv-input"
                    placeholder="Enter company name"
                    value={profileData.companyName}
                    onChange={handleChange}
                  />
                ),
              })}

              {renderField({
                label: "Business registration number / Tax id",
                Icon: FiFileText,
                locked: true,
                children: (
                  <input
                    type="text"
                    name="businessRegistrationNumber"
                    className="qv-input"
                    placeholder="Enter business registration number / Tax id"
                    value={profileData.businessRegistrationNumber}
                    disabled
                  />
                ),
              })}

              {renderField({
                label: "Phone no",
                Icon: FiPhone,
                children: (
                  <input
                    type="tel"
                    name="phone"
                    className="qv-input"
                    placeholder="Enter phone number"
                    value={profileData.phone}
                    onChange={handleChange}
                  />
                ),
              })}

              {renderField({
                label: "Headquarters address",
                Icon: FiMapPin,
                locked: true,
                children: (
                  <input
                    type="text"
                    name="headquartersAddress"
                    className="qv-input"
                    placeholder="Enter headquarters address"
                    value={profileData?.headquartersAddress}
                    readOnly
                  />
                ),
              })}
            </>
          )}

          {renderField({
            label: "Username",
            Icon: FiUser,
            children: (
              <input
                type="text"
                name={isAdmin ? "fullName" : "contactPersonName"}
                className="qv-input"
                placeholder="Enter username"
                value={isAdmin ? profileData.fullName : profileData.contactPersonName}
                onChange={handleChange}
              />
            ),
          })}

          {renderField({
            label: "Email",
            Icon: FiMail,
            locked: true,
            children: (
              <input
                type="email"
                name="email"
                className="qv-input"
                placeholder="Email"
                value={profileData.email}
                disabled
              />
            ),
          })}

          {renderField({
            label: "Date of birth",
            Icon: FiCalendar,
            children: (
              <div className="qpr-date">
                <DatePicker
                  selected={dobDate}
                  onChange={handleDateChange}
                  dateFormat="dd/MM/yyyy"
                  className="qv-input"
                  placeholderText="Select date of birth"
                  maxDate={maxDob()}
                  filterDate={(date) => date <= maxDob()}
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={80}
                />
              </div>
            ),
          })}
        </div>
      </section>

      <div className="qv-actions">
        <button
          type="button"
          className="qd-btn qd-btn-primary qv-submit"
          onClick={handleSave}
          disabled={saving || loading || uploading}
        >
          {saving ? (
            <>
              <span className="qo-spinner" /> Saving...
            </>
          ) : (
            <>
              <FiSave /> Save changes
            </>
          )}
        </button>
      </div>
    </div>
  );
}
